import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';

/**
 * Custom hook to fetch 28-day user trends from systemStats.
 * Returns arrays for unique hospitality reviewers, unique beer reviewers, and unique check-in users.
 */
function useUserTrends() {
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchTrendData = async () => {
      try {
        if (!cancelled) {
          setLoading(true);
          setError(null);
        }

        // Fetch last 28 daily entries ordered by date desc, then reverse to chronological
        const statsRef = collection(db, 'systemStats');
        const statsQuery = query(statsRef, orderBy('date', 'desc'), limit(28));
        const querySnapshot = await getDocs(statsQuery);

        if (cancelled) return;

        if (!querySnapshot.empty) {
          const data = querySnapshot.docs.map((doc) => {
            const docData = doc.data();
            return {
              date: docData.date?.toDate?.() || new Date(docData.date),
              uniqueUsersCount: docData.metrics?.uniqueUsersCount || 0,
              uniqueBeerReviewUsersCount:
                docData.metrics?.uniqueBeerReviewUsersCount || 0,
              uniqueCheckInUsersCount:
                docData.metrics?.uniqueCheckInUsersCount || 0,
            };
          });
          if (!cancelled) {
            setTrendData(data.reverse());
          }
        } else {
          if (!cancelled) {
            setTrendData([]);
          }
        }
      } catch (err) {
        console.error('Error fetching user trends:', err);
        if (!cancelled) {
          setError(err.message);
          setTrendData([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTrendData();

    return () => {
      cancelled = true;
    };
  }, []);

  return { trendData, loading, error };
}

export default useUserTrends;
